/**
 * Dependency-free math for the shared simulation.
 *
 * CRITICAL: this file (and everything in src/shared/) runs inside a Cloudflare Worker.
 * It must never import `three` or touch any DOM/browser API.
 *
 * Vectors are plain `{x, y, z}` objects so they serialize for free. Every operation
 * takes an explicit `out` parameter where practical so hot loops stay allocation-free.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
export const clone = (a: Vec3): Vec3 => ({ x: a.x, y: a.y, z: a.z });

export function set(out: Vec3, x: number, y: number, z: number): Vec3 {
  out.x = x;
  out.y = y;
  out.z = z;
  return out;
}

export function copy(out: Vec3, a: Vec3): Vec3 {
  out.x = a.x;
  out.y = a.y;
  out.z = a.z;
  return out;
}

export function add(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  out.x = a.x + b.x;
  out.y = a.y + b.y;
  out.z = a.z + b.z;
  return out;
}

export function sub(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  out.x = a.x - b.x;
  out.y = a.y - b.y;
  out.z = a.z - b.z;
  return out;
}

export function scale(out: Vec3, a: Vec3, s: number): Vec3 {
  out.x = a.x * s;
  out.y = a.y * s;
  out.z = a.z * s;
  return out;
}

/** out = a + b * s — the workhorse of any integrator. */
export function addScaled(out: Vec3, a: Vec3, b: Vec3, s: number): Vec3 {
  out.x = a.x + b.x * s;
  out.y = a.y + b.y * s;
  out.z = a.z + b.z * s;
  return out;
}

export function mul(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  out.x = a.x * b.x;
  out.y = a.y * b.y;
  out.z = a.z * b.z;
  return out;
}

export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

export function cross(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  const x = a.y * b.z - a.z * b.y;
  const y = a.z * b.x - a.x * b.z;
  const z = a.x * b.y - a.y * b.x;
  out.x = x;
  out.y = y;
  out.z = z;
  return out;
}

export const lengthSq = (a: Vec3): number => a.x * a.x + a.y * a.y + a.z * a.z;
export const length = (a: Vec3): number => Math.sqrt(lengthSq(a));

/** Horizontal (XZ) speed — used constantly by the movement code. */
export const lengthXZ = (a: Vec3): number => Math.sqrt(a.x * a.x + a.z * a.z);

export function distanceSq(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
}

export const distance = (a: Vec3, b: Vec3): number => Math.sqrt(distanceSq(a, b));

export function normalize(out: Vec3, a: Vec3): Vec3 {
  const len = length(a);
  if (len < EPSILON) return set(out, 0, 0, 0);
  return scale(out, a, 1 / len);
}

export function negate(out: Vec3, a: Vec3): Vec3 {
  out.x = -a.x;
  out.y = -a.y;
  out.z = -a.z;
  return out;
}

export function lerp(out: Vec3, a: Vec3, b: Vec3, t: number): Vec3 {
  out.x = a.x + (b.x - a.x) * t;
  out.y = a.y + (b.y - a.y) * t;
  out.z = a.z + (b.z - a.z) * t;
  return out;
}

/** Remove the component of `a` pointing along `normal`. Slide-along-surface, the core of collision response. */
export function projectOnPlane(out: Vec3, a: Vec3, normal: Vec3): Vec3 {
  const d = dot(a, normal);
  out.x = a.x - normal.x * d;
  out.y = a.y - normal.y * d;
  out.z = a.z - normal.z * d;
  return out;
}

/** Quake-style clip: reflect velocity off a plane with `overbounce` (1 = pure slide, 2 = full bounce). */
export function clipVelocity(out: Vec3, vel: Vec3, normal: Vec3, overbounce = 1.0): Vec3 {
  const backoff = dot(vel, normal) * overbounce;
  out.x = vel.x - normal.x * backoff;
  out.y = vel.y - normal.y * backoff;
  out.z = vel.z - normal.z * backoff;
  return out;
}

export function reflect(out: Vec3, dir: Vec3, normal: Vec3): Vec3 {
  const d = 2 * dot(dir, normal);
  out.x = dir.x - normal.x * d;
  out.y = dir.y - normal.y * d;
  out.z = dir.z - normal.z * d;
  return out;
}

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

export const EPSILON = 1e-6;
export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;
export const TAU = Math.PI * 2;

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
export const lerpN = (a: number, b: number, t: number): number => a + (b - a) * t;
export const sign = (v: number): number => (v < 0 ? -1 : v > 0 ? 1 : 0);

/** Map v from [inMin,inMax] to [outMin,outMax], clamped. */
export function remap(v: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  if (inMax - inMin < EPSILON) return outMin;
  return outMin + clamp01((v - inMin) / (inMax - inMin)) * (outMax - outMin);
}

export const smoothstep = (t: number): number => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};

export const smootherstep = (t: number): number => {
  const x = clamp01(t);
  return x * x * x * (x * (x * 6 - 15) + 10);
};

/**
 * Frame-rate independent exponential smoothing.
 * `rate` is roughly "fraction closed per second". Use this instead of naive
 * `a += (b - a) * 0.1` anywhere dt can vary, or the feel changes with framerate.
 */
export const damp = (a: number, b: number, rate: number, dt: number): number =>
  b + (a - b) * Math.exp(-rate * dt);

export function dampVec(out: Vec3, a: Vec3, b: Vec3, rate: number, dt: number): Vec3 {
  const f = Math.exp(-rate * dt);
  out.x = b.x + (a.x - b.x) * f;
  out.y = b.y + (a.y - b.y) * f;
  out.z = b.z + (a.z - b.z) * f;
  return out;
}

/** Shortest signed angular difference in radians, result in (-PI, PI]. */
export function angleDelta(a: number, b: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d <= -Math.PI) d += TAU;
  return d;
}

export function lerpAngle(a: number, b: number, t: number): number {
  return a + angleDelta(a, b) * t;
}

// ---------------------------------------------------------------------------
// Yaw/pitch <-> direction. Convention: yaw 0 looks down -Z, +yaw turns left (Y-up, right-handed).
// ---------------------------------------------------------------------------

export function dirFromYawPitch(out: Vec3, yaw: number, pitch: number): Vec3 {
  const cp = Math.cos(pitch);
  out.x = -Math.sin(yaw) * cp;
  out.y = Math.sin(pitch);
  out.z = -Math.cos(yaw) * cp;
  return out;
}

/** Flat forward vector (ignores pitch) — what movement input is relative to. */
export function forwardFromYaw(out: Vec3, yaw: number): Vec3 {
  out.x = -Math.sin(yaw);
  out.y = 0;
  out.z = -Math.cos(yaw);
  return out;
}

export function rightFromYaw(out: Vec3, yaw: number): Vec3 {
  out.x = Math.cos(yaw);
  out.y = 0;
  out.z = -Math.sin(yaw);
  return out;
}

// ---------------------------------------------------------------------------
// Deterministic PRNG (mulberry32). The sim must never call Math.random():
// server and client have to agree, and replays have to reproduce.
// ---------------------------------------------------------------------------

export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function rng() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
